import { useState, useId } from 'react';
import { ArrowIconLight, Css, Responsive } from '@/ui';
import styled from '@emotion/styled';
import { IJobEntry, JobType } from '../../../../contentful/types/IJob';
import { IJobsSearchState } from '../../hooks/useJobsSearch';
import { SearchInput } from '../../../../components/common/search-input/SearchInput';
import { EmployerSelect } from './EmployerSelect';
import { theme } from '../../../../styles/theme';
import { Shadow } from '../../../../styles/Shadow';
import Button from '../../../../components/common/button/Button';
import { JobTypeFilters } from './JobTypeFilters';

interface IJobSearchWidgetProps {
	jobs: IJobEntry[];
	state: IJobsSearchState;
	onSubmit: (state: Partial<IJobsSearchState>) => void;
}

export function JobSearchBar({ jobs, state, onSubmit }: IJobSearchWidgetProps) {
	const labelId = useId();
	const inputId = useId();

	const [queryValue, setQueryValue] = useState(state.query ?? '');

	const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setQueryValue(event.target.value);
	};

	const handleQueryClear = () => {
		setQueryValue('');
		onSubmit({ query: undefined, pageIndex: undefined });
	};

	const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		onSubmit({ query: queryValue, pageIndex: undefined });
	};

	const handleEmployerNameChange = (employerName?: string) => {
		onSubmit({ employerName, pageIndex: undefined });
	};

	const handleTypeChange = (type?: JobType[]) => {
		onSubmit({ type, pageIndex: undefined });
	};

	return (
		<Container>
			<StyledForm onSubmit={handleFormSubmit}>
				<Title>Job Search</Title>
				<Wrapper>
					<EmployerSelect
						jobs={jobs}
						value={state.employerName}
						onChange={handleEmployerNameChange}
					/>
					<SearchInput
						label="Search for jobs"
						value={queryValue}
						id={inputId}
						labelId={labelId}
						onChange={handleQueryChange}
						onClear={handleQueryClear}
					/>
					<StyledButton
						type="submit"
						label="Search"
						icon={<ArrowIconLight />}
						disabled={!queryValue}
					/>
				</Wrapper>
				<JobTypeFilters value={state.type} onChange={handleTypeChange} />
			</StyledForm>
		</Container>
	);
}

export const Container = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${Css.pxToRem(24)};
	background: ${theme.background?.default};
	border-radius: ${Css.pxToRem(16)};
	padding: ${Css.pxToRem(32)};
	padding-top: ${Css.pxToRem(24)};
	box-shadow: ${Shadow.Large};
	margin-top: -${Css.pxToRem(56)};
	margin-bottom: ${Css.pxToRem(40)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: ${Css.pxToRem(16)};
		gap: ${Css.pxToRem(16)};
		margin-top: -${Css.pxToRem(26)};
	}
`;

export const Title = styled.h2`
	font-size: ${Css.pxToRem(22)};
	margin: 0;
`;

export const StyledForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: ${Css.pxToRem(24)};
`;

const Wrapper = styled.div`
	position: relative;
	width: 100%;
	display: flex;
	flex-direction: row;
	gap: ${Css.pxToRem(16)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
		gap: ${Css.pxToRem(8)};
	}
`;

const StyledButton = styled(Button)`
	height: auto;
	white-space: nowrap;
	justify-content: center;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		align-self: flex-end;
	}
`;
